import { ThrowStmt } from '@angular/compiler';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, ValidatorFn, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatListOption } from '@angular/material/list';
import { CheckTimeSlot, QueueType, TimeSlot } from '@app-models/site';
import { SiteService } from '@app-services/site.service';
import { checkInvalid, CrossFieldErrorMatcher, ThreeFieldErrorMatcher } from 'src/app/validation';

@Component({
  selector: 'app-queuetype',
  templateUrl: './queuetype.component.html',
  styleUrls: ['./queuetype.component.scss']
})

export class QueuetypeComponent implements OnInit {

  selectedQueueType: QueueType;
  selectedIndex: number;
  queueTypes: QueueType[];
  slots: TimeSlot[];
  checkSlots: CheckTimeSlot[];
  openOnWeekends: boolean;
  otherOnline = false;
  noConsent = false;

  @ViewChild('form') formElementRef: ElementRef;
  fg: FormGroup;
  invalidRangeMatcher: ErrorStateMatcher = new CrossFieldErrorMatcher(
    'invalidRangeQueueNumber', 'overlabQueueNumber');

  invalidFastTrackRangeMatcher: ErrorStateMatcher = new ThreeFieldErrorMatcher(
    'invalidFastTrackRangeQueueNumber', 'fastTrackInitLessThanInit', 'fastTrackEndMoreThanEnd');

  constructor(
    public dialogRef: MatDialogRef<QueuetypeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private ss: SiteService
  ) {
    if (data) {
      this.selectedQueueType = data.selectedQueueType;
      this.selectedIndex = data.selectedIndex;
      this.queueTypes = data.allQueueTypes;
      this.noConsent = !data.activeConsentId;
      this.openOnWeekends = data.openOnWeekends;
    }
    if (this.queueTypes) {
      const foundIndex = this.queueTypes.findIndex(
        (queueType: QueueType) => queueType.isOnlineBookingAllowed);
      this.otherOnline = (foundIndex !== -1 && foundIndex !== this.selectedIndex);
    }
    const s = this.selectedQueueType || {};
    this.fg = this.fb.group({
      queueTypeId: [(s.queueTypeId === null || s.queueTypeId === undefined) ? null : s.queueTypeId, [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        this.validateDuplicateQueueTypeId()]],
      queueTypePrefix: [
        s.queueTypePrefix || null, [this.validateDuplicatePrefix()]],
      initialQueueNumber: [
        s.initialQueueNumber || null, [
          Validators.required,
          Validators.pattern('^[0-9]*$')]],
      endQueueNumber: [
        s.endQueueNumber || null, [
          Validators.required,
          Validators.pattern('^[0-9]*$')]],
      fastTrackInitialQueueNumber: new FormControl({
        value: s.fastTrackInitialQueueNumber || null,
        disabled: s.isOnlineBookingAllowed
      }, [
        Validators.pattern('^[0-9]*$')]),
      fastTrackEndQueueNumber: new FormControl({
        value: s.fastTrackEndQueueNumber || null,
        disabled: s.isOnlineBookingAllowed
      }, [
        Validators.pattern('^[0-9]*$')]),
      isOnlineBookingAllowed:
        new FormControl({
          value: s.isOnlineBookingAllowed || false,
          disabled: this.otherOnline || this.noConsent
        }),
      obMaxReservedQueues:
        new FormControl({
          value: s.obMaxReservedQueues || 5,
          disabled: !s.isOnlineBookingAllowed
        }, [Validators.required])
    }, {
      validators: [
        this.validateInvalidRange(),
        this.validateQueueOverlap(),
        this.validateFastTrackInvalidRange()]
    });

    this.slots = [];
    if (this.selectedQueueType && this.selectedQueueType.obOpenTimeSlots) {
      this.slots = [...this.selectedQueueType.obOpenTimeSlots];
    }
    this.checkSlots = this.ss.getAllCheckTimeSlots();
    for (const slot of this.slots) {
      const idx = this.checkSlots.findIndex((cslot: TimeSlot) => slot.key === cslot.key);
      if (idx !== -1) {
        this.checkSlots[idx].check = true;
      }
    }
    const prefixFC = this.fg.get('queueTypePrefix');
    prefixFC.valueChanges.subscribe({
      next: () => { prefixFC.patchValue(prefixFC.value.toUpperCase(), { emitEvent: false }); }
    });
  }

  ngOnInit(): void {
  }

  saveQueueType(): void {

    this.fg.markAsTouched();
    this.fg.markAsDirty();
    this.validateTimeSlot();
    if (!checkInvalid([this.fg], this.formElementRef, true)) {
      const queueType = Object.assign({}, this.fg.value);
      let prefix = queueType.queueTypePrefix;
      if (prefix) {
        prefix = prefix.trim();
      }
      if (prefix && prefix.length < 1) {
        prefix = null;
      }
      queueType.queueTypePrefix = prefix;
      queueType.obOpenTimeSlots = this.slots;
      queueType.obTimeslotSummary = this.ss.getTimeSlotSummary(
        this.slots, this.openOnWeekends);
      this.dialogRef.close(queueType);
    }
  }

  toggleOnlineForm() {
    if (this.fg.get('isOnlineBookingAllowed').value) {
      this.fg.get('obMaxReservedQueues').enable();
      this.fg.get('fastTrackInitialQueueNumber').disable();
      this.fg.get('fastTrackEndQueueNumber').disable();
      this.fg.get('fastTrackInitialQueueNumber').setValue(null);
      this.fg.get('fastTrackEndQueueNumber').setValue(null);

    } else {
      this.fg.get('obMaxReservedQueues').disable();
      this.fg.get('fastTrackInitialQueueNumber').enable();
      this.fg.get('fastTrackEndQueueNumber').enable();
    }
  }


  selectionChange(option: MatListOption) {
    const changedSlot: TimeSlot = option.value;
    const idx = this.slots.findIndex((slot: TimeSlot) => slot.key === changedSlot.key);
    if (option.selected) {
      if (idx !== -1) {
        console.warn('Error! Slot is already existed!');
      }
      this.slots.push({
        key: changedSlot.key,
        startHour: changedSlot.startHour,
        startMin: changedSlot.startMin,
        endHour: changedSlot.endHour,
        endMin: changedSlot.endMin,
      });
    } else {
      if (idx === -1) {
        console.warn('Error! Nothing to remove');
        return;
      }
      this.slots.splice(idx, 1);
    }

    this.validateTimeSlot();
  }

  formatSlot(slot: TimeSlot): string {
    return slot.key;
  }


  validateTimeSlot() {
    if (!this.fg) {
      return null;
    }
    const e = this.fg.errors;
    if (this.fg.get('isOnlineBookingAllowed').value) {
      if (this.slots && this.slots.length > 0) {
        if (e && e.hasOwnProperty('noTimeSlot')) {
          delete e.noTimeSlot;
          this.fg.setErrors(e);
        }
      } else {
        this.fg.setErrors({ noTimeSlot: { error: 'No slot' } });
      }
    } else {
      if (e && e.hasOwnProperty('noTimeSlot')) {
        delete e.noTimeSlot;
        this.fg.setErrors(e);
      }
    }
    return null;
  }

  public validateDuplicateQueueTypeId(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const typeId = control.value;
      if (!this.queueTypes) {
        return null;
      }
      const foundIndex = this.queueTypes.findIndex(
        (qType: QueueType) => qType.queueTypeId === typeId);
      if (foundIndex === -1 || foundIndex === this.selectedIndex) {
        return null;
      } else {
        return { duplicateQueueTypeId: { queueTypeId: control.value } };
      }
    };
  }

  public validateDuplicatePrefix(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let prefix = control.value;
      if (!this.queueTypes) {
        return null;
      }
      if (typeof prefix !== 'undefined' && prefix != null) {
        prefix = prefix.trim();
        if (prefix.length < 1) {
          // control.setValue(null);
          return null;
        }
        // control.setValue(prefix, { emitEvent: false });
      }

      if ((prefix == null) || (prefix && (prefix.length < 1))) {
        return null;
      }

      const foundIndex = this.queueTypes.findIndex(
        (qType: QueueType) => (qType.queueTypePrefix === prefix));
      if (foundIndex === -1 || foundIndex === this.selectedIndex) {
        return null;
      } else {
        return { duplicatePrefix: { prefix: control.value } };
      }
    };
  }


  public validateInvalidRange(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!this.fg) {
        return null;
      }
      const initV = this.fg.get('initialQueueNumber').value;
      const endV = this.fg.get('endQueueNumber').value;
      if (!initV || !endV) {
        return null;
      }
      if (initV >= endV) {
        return { invalidRangeQueueNumber: { initQueueNumber: initV, endQueueNumber: endV } };
      }
      return null;
    };
  }

  public validateFastTrackInvalidRange(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!this.fg) {
        return null;
      }
      const initV = this.fg.get('initialQueueNumber').value;
      const endV = this.fg.get('endQueueNumber').value;
      const initFastV = this.fg.get('fastTrackInitialQueueNumber').value;
      const endFastV = this.fg.get('fastTrackEndQueueNumber').value;
      const error = {};
      if ((initFastV && !endFastV) || (!initFastV && endFastV)) {
        Object.assign(error, { invalidFastTrackRangeQueueNumber: { initQueueNumber: initFastV, endQueueNumber: endFastV } });
      }
      if (initFastV && endFastV && initFastV >= endFastV) {
        Object.assign(error, { invalidFastTrackRangeQueueNumber: { initQueueNumber: initFastV, endQueueNumber: endFastV } });
      }
      if (initFastV && initV && initFastV < initV) {
        Object.assign(error, { fastTrackInitLessThanInit: { initQueueNumber: initV, fInitQueueNumber: initFastV } });
      }
      if (endFastV && endV && endFastV > endV) {
        Object.assign(error, { fastTrackEndMoreThanEnd: { endQueueNumber: endV, fEndQueueNumber: endFastV } });
      }
      // console.log(error);
      // this.fg.setErrors(error);
      if (Object.keys(error).length === 0 && error.constructor === Object) {
        return null;
      } else {
        return error;
      }

    };
  }

  public validateQueueOverlap(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!this.fg) {
        return null;
      }
      if (!this.queueTypes) {
        return null;
      }
      const num = control.value;
      const initV = this.fg.get('initialQueueNumber').value;
      const endV = this.fg.get('endQueueNumber').value;
      if (!initV || !endV) {
        return null;
      }
      let i = -1;
      for (const qType of this.queueTypes) {
        i += 1;
        if (i === this.selectedIndex) {
          continue;
        }
        if (initV <= qType.endQueueNumber && endV >= qType.initialQueueNumber) {
          // overlap
          return { overlabQueueNumber: { initQueueNumber: initV, endQueueNumber: endV } };
        }
      }
      return null;
    };
  }

}
